import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import "../styles/Analyze.css";
import ImageCropper from "../components/ImageCropper";
import roiWrongImg from "../assets/analyze/roi-wrong.png";
import roiCorrectImg from "../assets/analyze/roi-correct.png";
import API from "../utils/axiosInstance";
import {
  FiUploadCloud,
  FiScissors,
  FiTrash2,
  FiPlay,
  FiX,
  FiBarChart2,
  FiRotateCcw,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";

const ENDPOINT_FULL = "/api/analyze/full/";
// Gösterilecek en fazla sonuç; backend 1..MAX_RESULTS arası, skora göre sıralı döndürür.
const MAX_RESULTS = 5;
// Backend'in 422 yanıtındaki makine okunur "code" -> i18n anahtarı. Bilinmeyen kodlar genel akışa düşer.
const ANALYSIS_ERROR_KEYS = {
  no_reliable_match: "analyze.errors.noReliableMatch",
  low_resolution: "analyze.errors.lowResolution",
};

export default function AnalyzePage() {
  const { t } = useTranslation();

  const [imageFile, setImageFile]       = useState(null);
  const [imageUrl, setImageUrl]         = useState("");
  const [rois, setRois]                 = useState([]);
  const [activeRoiId, setActiveRoiId]   = useState(null);

  const [busy, setBusy]                 = useState(false);
  const [hasRun, setHasRun]             = useState(false);

  // Ekranda gösterilen eşleşme satırları — ["Nobel Biocare — NobelActive", ...]
  const [combinedList, setCombinedList] = useState([]);

  const [lastRoiBackup, setLastRoiBackup] = useState(null);

  const isAnalyzingRef = useRef(false);
  const abortRef       = useRef(null);
  const inputRef       = useRef(null);
  const runIdRef       = useRef(0);

  // Sayfa kapatma koruması
  useEffect(() => {
    const handler = (e) => {
      if (busy) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [busy]);

  // ── Yardımcılar ──────────────────────────────────────────────────────

  const resetAll = useCallback(() => {
    setCombinedList([]);
    setHasRun(false);
    setBusy(false);
    runIdRef.current += 1;
  }, []);

  const cleanAgentText = (text = "") =>
    String(text)
      .replace(/nearest catalog match/gi, "")
      .replace(/catalog match/gi, "")
      .replace(/nearest implant match/gi, "")
      .replace(/implant match/gi, "")
      .replace(/nearest match/gi, "")
      .replace(/best match/gi, "")
      .replace(/catalog/gi, "")
      .replace(/match/gi, "")
      .replace(/[:\-–—]+$/g, "")
      .replace(/\s+/g, " ")
      .trim();

  // ── Form builder ─────────────────────────────────────────────────────

  const buildFormData = (roi) => {
    if (!roi) throw new Error(t("analyze.errors.roiNotSelected"));

    const x = Number(roi.x);
    const y = Number(roi.y);
    const w = Number(roi.w);
    const h = Number(roi.h);

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(w) || !Number.isFinite(h))
      throw new Error(t("analyze.errors.roiCorrupted"));
    if (w <= 20 || h <= 20)
      throw new Error(t("analyze.errors.roiTooSmall"));

    if (!imageFile) throw new Error(t("analyze.errors.originalFileMissing"));

    const form = new FormData();
    form.append("image", imageFile, imageFile.name);
    form.append("crop_x", String(Math.round(x)));
    form.append("crop_y", String(Math.round(y)));
    form.append("crop_w", String(Math.round(w)));
    form.append("crop_h", String(Math.round(h)));
    return form;
  };

  const onPickFile = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
    setImageUrl(URL.createObjectURL(f));
    setRois([]);
    setActiveRoiId(null);
    setLastRoiBackup(null);
    resetAll();
  }, [resetAll]);

  const onClearImage = () => {
    setImageFile(null);
    setImageUrl("");
    setRois([]);
    setActiveRoiId(null);
    setLastRoiBackup(null);
    resetAll();
    if (inputRef.current) inputRef.current.value = "";
  };

  const onAddRoi = useCallback((roi) => {
    const rawX = Number(roi.x);
    const rawY = Number(roi.y);
    const rawW = Number(roi.w);
    const rawH = Number(roi.h);

    const fixedRoi = {
      ...roi,
      x: rawW < 0 ? rawX + rawW : rawX,
      y: rawH < 0 ? rawY + rawH : rawY,
      w: Math.abs(rawW),
      h: Math.abs(rawH),
    };

    if (
      !Number.isFinite(fixedRoi.x) || !Number.isFinite(fixedRoi.y) ||
      !Number.isFinite(fixedRoi.w) || !Number.isFinite(fixedRoi.h) ||
      fixedRoi.w <= 20 || fixedRoi.h <= 20 || !fixedRoi.previewBlob
    ) {
      alert(t("analyze.errors.roiInvalid"));
      return;
    }

    const id         = Math.random().toString(36).slice(2);
    const previewUrl = URL.createObjectURL(fixedRoi.previewBlob);
    const current    = rois[0] || null;

    setLastRoiBackup(current ? { ...current } : null);
    setRois([{ id, ...fixedRoi, previewUrl, name: "ROI-1" }]);
    setActiveRoiId(id);
    resetAll();
  }, [rois, resetAll]);

  const onRemoveRoi = (id) => {
    const current = rois.find((r) => r.id === id) || null;
    if (current) setLastRoiBackup({ ...current });
    setRois([]);
    if (activeRoiId === id) setActiveRoiId(null);
    resetAll();
  };

  const onUndo = () => {
    if (!lastRoiBackup) return;
    const restored = { ...lastRoiBackup };
    setRois([restored]);
    setActiveRoiId(restored.id);
    setLastRoiBackup(null);
    resetAll();
  };

  // ── Ana analiz ────────────────────────────────────────────────────────

  const runAnalysis = async () => {
    if (!rois.length) { alert(t("analyze.alert.selectRoi")); return; }
    if (isAnalyzingRef.current) return;

    isAnalyzingRef.current = true;
    const myRunId = ++runIdRef.current;

    setBusy(true);
    setHasRun(true);
    setCombinedList([]);

    if (abortRef.current) {
      try { abortRef.current.abort(); } catch {}
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const roi = rois[0];

    try {
      const form = buildFormData(roi);

      const res = await API.post(ENDPOINT_FULL, form, {
        signal:  controller.signal,
        timeout: 600_000,
      });

      if (runIdRef.current !== myRunId) return;

      const agentMatches = Array.isArray(res?.data?.agent_matches)
        ? res.data.agent_matches
        : [];

      const out = [];

      for (const m of agentMatches.slice(0, MAX_RESULTS)) {
        const brand = cleanAgentText(String(m?.brand || "").trim());
        const model = cleanAgentText(String(m?.model || "").trim());
        if (brand && model) out.push({ label: `${brand} — ${model}` });
        else if (brand)     out.push({ label: brand });
      }

      if (out.length === 0) {
        throw new Error(t("analyze.errors.analysisFailed"));
      }

      setCombinedList(out);

      // Mail raporu — backend'e gönder (kredi düştükten sonra)
      try {
        const reader = new FileReader();
        reader.onloadend = async () => {
          await API.post("/api/report/", {
            roi_image: reader.result,
            results:   out.map((r) => r.label),
          });
        };
        reader.readAsDataURL(roi.previewBlob);
      } catch (mailErr) {
        console.warn("Mail raporu gönderilemedi:", mailErr);
      }

    } catch (err) {
      if (runIdRef.current !== myRunId) return;

      const detail        = err?.response?.data?.detail || "";
      const errorCode     = err?.response?.data?.code || "";
      const httpStatus    = String(err?.response?.status || "");
      const isAbort       = err?.name === "AbortError" || err?.name === "CanceledError";

      if (isAbort) {
        // Kullanıcı sayfadan ayrıldı vs. — sessizce çık
      } else if (Object.prototype.hasOwnProperty.call(ANALYSIS_ERROR_KEYS, errorCode)) {
        // Agent analiz yaptı ama sonuç döndüremedi; kredi düşmedi.
        alert(t(ANALYSIS_ERROR_KEYS[errorCode]));
      } else if (httpStatus === "403" || detail.toLowerCase().includes("krediniz yok")) {
        alert(t("analyze.errors.noCredit"));
      } else if (detail) {
        alert(detail);
      } else {
        alert(t("analyze.alert.genericError"));
      }

      setCombinedList([]);
    } finally {
      if (runIdRef.current === myRunId) {
        setBusy(false);
        isAnalyzingRef.current = false;
        if (abortRef.current === controller) abortRef.current = null;
      }
    }
  };

  // ── Türetilmiş durum ──────────────────────────────────────────────────

  const hasAnyResult          = combinedList.length > 0;
  const isWaitingAllResults   = hasRun && busy;
  const hasFinishedButEmpty   = hasRun && !busy && combinedList.length === 0;

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="analyze">
      <aside className="analyze__sidebar">
        <div className="title-row">
          <FiBarChart2 className="title-icon" />
          <h2 className="title">{t("analyze.title")}</h2>
        </div>

        <div className="card upload">
          <label className="upload__label">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png"
              onChange={onPickFile}
              hidden
            />
            <FiUploadCloud />
            <span>{t("analyze.upload.label")}</span>
          </label>

          {imageUrl && (
            <button className="btn btn-ghost danger" onClick={onClearImage}>
              <FiX /> {t("analyze.clearImage")}
            </button>
          )}

          <p className="hint">{t("analyze.hint")}</p>
        </div>

        <div className="card roi-list">
          <div className="card__head roi-head">
            <div className="roi-head__title-wrap">
              <h3 className="roi-head__title">{t("analyze.roi.title")}</h3>
              {rois.length > 0 && <span className="roi-head__count">1</span>}
            </div>

            <div className="roi-head__actions">
              <button
                className="btn btn-ghost roi-action-btn"
                onClick={onUndo}
                disabled={!lastRoiBackup}
                title={t("analyze.roi.undoTitle")}
                type="button"
              >
                <FiRotateCcw />
                <span>{t("analyze.roi.undo")}</span>
              </button>

              {rois.length > 0 && (
                <button
                  className="btn btn-ghost roi-icon-btn"
                  onClick={() => onRemoveRoi(rois[0].id)}
                  title={t("analyze.roi.remove")}
                  aria-label={t("analyze.roi.remove")}
                  type="button"
                >
                  <FiTrash2 />
                </button>
              )}
            </div>
          </div>

          <ul className="roi-items">
            {rois.length > 0 ? (
              <li
                key={rois[0].id}
                className={`roi-item ${activeRoiId === rois[0].id ? "active" : ""}`}
                onClick={() => setActiveRoiId(rois[0].id)}
              >
                <div className="roi-name-under">{rois[0].name}</div>
                <img src={rois[0].previewUrl} alt={rois[0].name} />
              </li>
            ) : (
              <li className="empty">{t("analyze.roi.empty")}</li>
            )}
          </ul>
        </div>

        <div className="card run">
          <button
            className="btn btn-primary"
            disabled={!rois.length || busy}
            onClick={runAnalysis}
          >
            <FiPlay /> {busy ? t("analyze.running") : t("analyze.start")}
          </button>

          <div className="roi-guide-box">
            <div className="roi-example-guide">
              <div className="roi-example-card wrong">
                <div className="roi-example-label">❌ {t("analyze.roi.exampleWrong")}</div>
                <div className="roi-example-image-box">
                  <img src={roiWrongImg} alt={t("analyze.roi.exampleWrongAlt")} />
                </div>
              </div>

              <div className="roi-example-arrow">→</div>

              <div className="roi-example-card correct">
                <div className="roi-example-label">✅ {t("analyze.roi.exampleCorrect")}</div>
                <div className="roi-example-image-box">
                  <img src={roiCorrectImg} alt={t("analyze.roi.exampleCorrectAlt")} />
                </div>
              </div>
            </div>

            <p className="roi-guide-text">{t("analyze.roi.guideText")}</p>
          </div>
        </div>
      </aside>

      <main className="analyze__main">
        <div className="workspace">
          {imageUrl ? (
            <ImageCropper
              imageUrl={imageUrl}
              onAddRoi={onAddRoi}
              highlightRoiId={activeRoiId}
            />
          ) : (
            <div className="placeholder">
              <FiScissors />
              <p>{t("analyze.placeholder")}</p>
            </div>
          )}
        </div>

        <section className="results">
          <h3 className="results__title">{t("analyze.results.title")}</h3>

          {!rois.length ? (
            <div className="results__empty">{t("analyze.results.uploadFirst")}</div>
          ) : (
            <div className="result-block">
              <div className="result-block__head">
                <img src={rois[0].previewUrl} alt={rois[0].name} />
                <div>
                  <div className="roi-title">{rois[0].name || t("analyze.results.roiFallback")}</div>
                  <div className="roi-sub">
                    {Math.round(rois[0].w || 0)}×{Math.round(rois[0].h || 0)} px
                  </div>
                </div>
              </div>

              <div className="simple-result-list" style={{ marginTop: 0 }}>
                <h4 className="result-headline">
                  {t("analyze.results.matchedModels")}{" "}
                  {hasAnyResult && (
                                      <span style={{ color: "#e53e3e", fontWeight: 400, fontSize: "0.78rem" }}>
                    ({t("analyze.results.unsortedPrefix")} <strong>{t("analyze.results.unsortedStrong")}</strong>)
                  </span>
                  )}
                </h4>

                {!hasRun ? (
                  <div style={{ height: 12 }} />
                ) : isWaitingAllResults ? (
                  <>
                    <div className="warning-text">{t("analyze.results.waitingWarning")}</div>
                    <div className="results__empty">⏳ {t("analyze.results.preparing")}</div>
                  </>
                ) : hasAnyResult ? (
                  <ul className="result-list">
                    {combinedList.map((item, idx) => (
                      <li key={idx} className="result-item">
                        <div style={{ fontWeight: 800 }}>
                          {idx + 1}. {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : hasFinishedButEmpty ? (
                  <div className="results__empty">{t("analyze.results.notFound")}</div>
                ) : (
                  <div style={{ height: 12 }} />
                )}

                {hasRun && !busy && rois.length > 0 && (
                  <div className="retry-result-section">
                    <button
                      type="button"
                      className="btn btn-ghost retry-btn"
                      onClick={() => {
                        onClearImage();
                        inputRef.current?.click();
                      }}
                    >
                      {t("analyze.results.retry")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
